import type { FC } from "react";
import { memo, useEffect, useState } from "react";

import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { useLocale } from "@jti/app-context/useLocale";
import { groupOrderAttributesByAddressPlacement } from "@jti/commerce";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { Box } from "@jti/core-components/components/Box";
import { Card } from "@jti/core-components/components/Card";
import { Separator } from "@jti/core-components/components/Separator";
import type { IOrderAttributeOutput, Order } from "@jti/magento";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectOrderAttributes } from "@jti/store/selectors/selectOrderAttributes";
import { fetchOrderAttributes } from "@jti/store/slices/LEGACY__checkoutSlice";
import { DEPRECATED_Loading, formatDate, Typography, useTranslations } from "@jti/ui";

import Prices from "@checkout/components/Prices/Prices";
import { useOrder } from "@checkout/hooks";

import { SummaryItems } from "../SummaryItems";

import { OrderAddresses } from "./OrderAddress";
import { OrderDotContent } from "./OrderDotContent";
import OrderPaymentMethod from "./OrderPaymentMethod";

import styles from "./OrderView.module.scss";

const { sanitize } = dompurify;

const formatAddressForDisplay = (address: NonNullable<Order["shippingAddress"]>) => {
  const regionName = typeof address.region === "string" ? address.region : (address.region as any)?.region;

  return {
    ...address,
    region: regionName
      ? {
          region_id: 0,
          region_code: "0",
          region: regionName,
        }
      : undefined,
  };
};

export const OrderView: FC = memo(() => {
  const { zeroValueOrder, orderSummaryConfig } = useCheckoutConfiguration();
  const zeroValueOrderNotification = zeroValueOrder?.userNotification || "";
  const { COUNTRY_NAME, dateFormat } = useConfig();
  const { orderFetchStatus, order, paymentMethod, isFreeTrial } = useOrder();
  const locale = useLocale();
  const { __ } = useTranslations();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchOrderAttributes(isFreeTrial));
  }, [dispatch, isFreeTrial]);

  const {
    displayBillingAddress,
    displayDeliveryMethod,
    displayItemsSummary,
    displayPaymentMethod,
    displayShippingAddress,
  } = orderSummaryConfig;

  const orderAttributes = useSelector(selectOrderAttributes);
  const [shippingAttributes, setShippingAttributes] = useState<IOrderAttributeOutput[]>([]);
  const [billingAttributes, setBillingAttributes] = useState<IOrderAttributeOutput[]>([]);

  useEffect(() => {
    if (order?.additional_attributes && orderAttributes.length) {
      const { updatedShippingAttributes, updatedBillingAttributes } = groupOrderAttributesByAddressPlacement(
        order.additional_attributes,
        orderAttributes
      );

      if (updatedShippingAttributes) {
        setShippingAttributes(updatedShippingAttributes);
      }
      if (updatedBillingAttributes) {
        setBillingAttributes(updatedBillingAttributes);
      }
    }
  }, [order?.additional_attributes, orderAttributes.length, orderAttributes]);

  const shouldDisplayBoxSection =
    displayBillingAddress || displayDeliveryMethod || displayPaymentMethod || displayShippingAddress;

  if (orderFetchStatus === "pending") return <DEPRECATED_Loading />;

  return (
    <>
      <div className={styles.numberDateContainer} data-testid="order-basic-info-section">
        <div className={styles.numberDate}>
          <Box data-testid="order-info-box-number" title={__("cart.order.number")} variant="level-2">
            <h3 className={styles.orderNumber} data-testid="order-info-box-title">
              {__("cart.order.number")}
            </h3>
            <Typography variant="body1" dataTestId="order-info-box-value">
              <span>#</span>
              <span>{order?.number || "000000000"}</span>
            </Typography>
          </Box>
        </div>

        <div className={styles.numberDate}>
          <Box data-testid="order-info-box-date" title={__("cart.order.date")} variant="level-2">
            <h3 className={styles.orderDate} data-testid="order-info-box-title">
              {__("cart.order.date")}
            </h3>
            <Typography variant="body1" dataTestId="order-info-box-value">
              {formatDate(order?.orderDate, dateFormat || "ddd D MMMM YYYY", locale, true) || "..."}
            </Typography>
          </Box>
        </div>
      </div>

      {displayItemsSummary && <SummaryItems items={order?.items || []} />}

      <Card title={__("cart.order.summary.noItemsTitle")} variant="level-2">
        <div className={styles.separator}>
          <Separator />
        </div>
        <Prices isSummary prices={order?.prices} />
      </Card>

      {shouldDisplayBoxSection && (
        <div className={styles.boxes}>
          {displayShippingAddress && order?.shippingAddress && (
            <OrderAddresses
              address={{
                ...formatAddressForDisplay(order.shippingAddress),
                countryName: COUNTRY_NAME, // DESP-65624
              }}
              className={styles.shipping}
              title={"cart.order.shipping_address.title"}
              orderAttributesInfos={shippingAttributes}
            />
          )}

          {displayDeliveryMethod && (
            <OrderDotContent
              condition={!!order?.shippingMethod?.methodCode}
              className={styles.delivery}
              title={"cart.order.delivery_method.title"}
            >
              {order?.shippingMethod?.methodCode}
            </OrderDotContent>
          )}

          {displayBillingAddress && order?.billingAddress && (
            <OrderAddresses
              address={{
                ...formatAddressForDisplay(order.billingAddress),
                countryName: COUNTRY_NAME, // DESP-65624
              }}
              className={styles.billing}
              title={"cart.order.billing_address.title"}
              orderAttributesInfos={billingAttributes}
            />
          )}

          {displayPaymentMethod && (
            <div className={styles.payment}>
              <Box title={__("cart.order.payment_method.title")} variant="level-2">
                {order?.isZeroValueOrder ? (
                  <div
                    data-testid="pay-on-delivery-message"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(zeroValueOrderNotification),
                    }}
                  ></div>
                ) : (
                  <OrderPaymentMethod paymentMethod={paymentMethod} />
                )}
              </Box>
            </div>
          )}
        </div>
      )}
    </>
  );
});
